/**
 * PrimeArtifactCompletenessDashboard Component
 * 
 * Shows the completeness status of a prime artifact:
 * - Overall completeness percentage
 * - What's missing (properties, states, transitions, processes)
 * - Quick actions to generate missing pieces
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Progress,
  Space,
  Tag,
  Button,
  Typography,
  Collapse,
  List,
  Tooltip,
  Alert,
  Spin,
  Badge,
  Row,
  Col
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  WarningOutlined,
  ThunderboltOutlined,
  ReloadOutlined,
  PropertySafetyOutlined,
  BranchesOutlined,
  SwapOutlined,
  SettingOutlined
} from '@ant-design/icons';
import { apiClient } from '../../services/apiClient';

const { Title, Text } = Typography;
const { Panel } = Collapse;

interface CompletenessItem {
  name: string;
  status: 'complete' | 'incomplete' | 'missing';
  details?: string;
}

interface CompletenessSection {
  name: string;
  items: CompletenessItem[];
  total: number;
  complete: number;
  percentage: number;
}

interface CompletenessData {
  overall_percentage: number;
  is_complete: boolean;
  sections: {
    properties: CompletenessSection;
    states: CompletenessSection;
    transitions: CompletenessSection;
    processes: CompletenessSection;
  };
  missing_items: {
    properties: string[];
    states: string[];
    transitions: string[];
    processes: string[];
  };
  recommendations: string[];
}

interface PrimeArtifactCompletenessDashboardProps {
  artifactId: string;
  artifactName?: string;
  onGenerateClick?: () => void;
  compact?: boolean;
}

export const PrimeArtifactCompletenessDashboard: React.FC<PrimeArtifactCompletenessDashboardProps> = ({
  artifactId,
  artifactName,
  onGenerateClick,
  compact = false
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<CompletenessData | null>(null);

  const fetchCompleteness = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(`/prime-artifacts/${artifactId}/completeness`);
      setData(response.data);
    } catch (err: any) {
      console.error('Error fetching completeness:', err);
      // If endpoint doesn't exist, create mock data from summary endpoint
      try {
        const summaryResponse = await apiClient.get(`/prime-artifacts/${artifactId}/completeness/summary`);
        const summary = summaryResponse.data;
        
        // Create completeness data from summary
        setData({
          overall_percentage: summary.percentage || 0,
          is_complete: summary.is_complete || false,
          sections: {
            properties: {
              name: 'Properties',
              items: [],
              total: summary.properties?.total || 0,
              complete: summary.properties?.complete || 0,
              percentage: summary.properties?.total ? Math.round((summary.properties.complete / summary.properties.total) * 100) : 0
            },
            states: {
              name: 'States',
              items: [],
              total: summary.states?.total || 0,
              complete: summary.states?.complete || 0,
              percentage: summary.states?.total ? Math.round((summary.states.complete / summary.states.total) * 100) : 0
            },
            transitions: {
              name: 'Transitions',
              items: [],
              total: summary.transitions?.total || 0,
              complete: summary.transitions?.complete || 0,
              percentage: summary.transitions?.total ? Math.round((summary.transitions.complete / summary.transitions.total) * 100) : 0
            },
            processes: {
              name: 'Processes',
              items: [],
              total: summary.processes?.total || 0,
              complete: summary.processes?.complete || 0,
              percentage: summary.processes?.total ? Math.round((summary.processes.complete / summary.processes.total) * 100) : 0
            }
          },
          missing_items: {
            properties: summary.missing_properties || [],
            states: summary.missing_states || [],
            transitions: summary.missing_transitions || [],
            processes: summary.missing_processes || []
          },
          recommendations: summary.recommendations || []
        });
      } catch (summaryErr: any) {
        // If both endpoints fail, show counts from properties/states endpoints
        setError('Unable to fetch completeness data');
      }
    } finally {
      setLoading(false);
    }
  }, [artifactId]);

  useEffect(() => {
    void fetchCompleteness();
  }, [fetchCompleteness]);

  if (loading) {
    return (
      <Card size={compact ? 'small' : 'default'}>
        <div style={{ textAlign: 'center', padding: compact ? 8 : 24 }}>
          <Spin tip="Loading completeness..." />
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card size={compact ? 'small' : 'default'}>
        <Alert
          message="Unable to load completeness data"
          description={error}
          type="warning"
          showIcon
        />
      </Card>
    );
  }

  if (!data) {
    return null;
  }

  // Get status color and icon
  const getStatusColor = (percentage: number) => {
    if (percentage >= 100) return '#52c41a';
    if (percentage >= 75) return '#faad14';
    if (percentage >= 50) return '#fa8c16';
    return '#ff4d4f';
  };

  const getStatusIcon = (percentage: number) => {
    if (percentage >= 100) return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
    if (percentage >= 50) return <WarningOutlined style={{ color: '#faad14' }} />;
    return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
  };

  const getSectionIcon = (section: string) => {
    switch (section) {
      case 'properties': return <PropertySafetyOutlined />;
      case 'states': return <BranchesOutlined />;
      case 'transitions': return <SwapOutlined />;
      case 'processes': return <SettingOutlined />;
      default: return null;
    }
  };

  // Compact view for embedding in other components
  if (compact) {
    return (
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Space>
            {getStatusIcon(data.overall_percentage)}
            <Text strong>Completeness</Text>
            <Progress 
              percent={data.overall_percentage} 
              size="small" 
              style={{ width: 100 }}
              strokeColor={getStatusColor(data.overall_percentage)}
            />
          </Space>
          <Space>
            <Tooltip title="Refresh">
              <Button size="small" icon={<ReloadOutlined />} onClick={fetchCompleteness} />
            </Tooltip>
            {!data.is_complete && onGenerateClick && (
              <Button size="small" type="primary" icon={<ThunderboltOutlined />} onClick={onGenerateClick}>
                Generate
              </Button>
            )}
          </Space>
        </Space>
      </Card>
    );
  }

  // Full dashboard view
  return (
    <Card 
      title={
        <Space>
          <span>Completeness Dashboard</span>
          {artifactName && <Tag color="blue">{artifactName}</Tag>}
        </Space>
      }
      extra={
        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetchCompleteness}>
            Refresh
          </Button>
          {!data.is_complete && onGenerateClick && (
            <Button type="primary" icon={<ThunderboltOutlined />} onClick={onGenerateClick}>
              Generate Missing
            </Button>
          )}
        </Space>
      }
    >
      {/* Overall Progress */}
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <Progress
          type="circle"
          percent={data.overall_percentage}
          strokeColor={getStatusColor(data.overall_percentage)}
          format={(percent) => (
            <span>
              {percent}%
              <br />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {data.is_complete ? 'Complete' : 'Incomplete'}
              </Text>
            </span>
          )}
        />
      </div>

      {/* Section Progress */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {Object.entries(data.sections).map(([key, section]) => (
          <Col span={6} key={key}>
            <Card size="small" style={{ textAlign: 'center' }}>
              <Space direction="vertical">
                {getSectionIcon(key)}
                <Text strong>{section.name}</Text>
                <Progress
                  percent={section.percentage}
                  size="small"
                  strokeColor={getStatusColor(section.percentage)}
                />
                <Text type="secondary">
                  {section.complete} / {section.total}
                </Text>
              </Space>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Missing Items */}
      {!data.is_complete && (
        <Collapse ghost>
          {Object.entries(data.missing_items).map(([key, items]) => {
            if (!items || items.length === 0) return null;
            const section = data.sections[key as keyof typeof data.sections];
            return (
              <Panel
                key={key}
                header={
                  <Space>
                    {getSectionIcon(key)}
                    <span>Missing {section.name}</span>
                    <Badge count={items.length} style={{ backgroundColor: '#ff4d4f' }} />
                  </Space>
                }
              >
                <List
                  size="small"
                  dataSource={items}
                  renderItem={(item) => (
                    <List.Item>
                      <CloseCircleOutlined style={{ color: '#ff4d4f', marginRight: 8 }} />
                      {item}
                    </List.Item>
                  )}
                />
              </Panel>
            );
          })}
        </Collapse>
      )}

      {/* Recommendations */}
      {data.recommendations && data.recommendations.length > 0 && (
        <Alert
          message="Recommendations"
          description={
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {data.recommendations.map((rec, idx) => (
                <li key={idx}>{rec}</li>
              ))}
            </ul>
          }
          type="info"
          showIcon
          style={{ marginTop: 16 }}
        />
      )}

      {/* Complete message */}
      {data.is_complete && (
        <Alert
          message="Artifact Definition Complete"
          description="This prime artifact has all required properties, states, transitions, and processes defined."
          type="success"
          showIcon
          style={{ marginTop: 16 }}
        />
      )}
    </Card>
  );
};

export default PrimeArtifactCompletenessDashboard;
